import moment from 'moment-timezone'
import {
  dateTime as SharedDateTime,
  fees as SharedFees,
} from '@blocktickets/shared-utils'

export const COUNTDOWN_NUM = 9
export const COUNTDOWN_DURATION = 1000

export const isCartExpiredResponse = (err) => err?.response?.status === 410

// global state options
export const stateOpt = [
  { value: 'AK', name: 'Alaska' },
  { value: 'TX', name: 'Texas' },
  { value: 'AL', name: 'Alabama' },
  { value: 'AR', name: 'Arkansas' },
  { value: 'AZ', name: 'Arizona' },
  { value: 'CA', name: 'California' },
  { value: 'CO', name: 'Colorado' },
  { value: 'CT', name: 'Connecticut' },
  { value: 'DC', name: 'District Of Columbia' },
  { value: 'DE', name: 'Delaware' },
  { value: 'FL', name: 'Florida' },
  { value: 'GA', name: 'Georgia' },
  { value: 'HI', name: 'Hawaii' },
  { value: 'IA', name: 'Iowa' },
  { value: 'ID', name: 'Idaho' },
  { value: 'IL', name: 'Illinois' },
  { value: 'IN', name: 'Indiana' },
  { value: 'KS', name: 'Kansas' },
  { value: 'KY', name: 'Kentucky' },
  { value: 'LA', name: 'Louisiana' },
  { value: 'MA', name: 'Massachusetts' },
  { value: 'MD', name: 'Maryland' },
  { value: 'ME', name: 'Maine' },
  { value: 'MI', name: 'Michigan' },
  { value: 'MN', name: 'Minnesota' },
  { value: 'MO', name: 'Missouri' },
  { value: 'MS', name: 'Mississippi' },
  { value: 'MT', name: 'Montana' },
  { value: 'NC', name: 'North Carolina' },
  { value: 'ND', name: 'North Dakota' },
  { value: 'NE', name: 'Nebraska' },
  { value: 'NH', name: 'New Hampshire' },
  { value: 'NJ', name: 'New Jersey' },
  { value: 'NM', name: 'New Mexico' },
  { value: 'NV', name: 'Nevada' },
  { value: 'NY', name: 'New York' },
  { value: 'OH', name: 'Ohio' },
  { value: 'OK', name: 'Oklahoma' },
  { value: 'OR', name: 'Oregon' },
  { value: 'PA', name: 'Pennsylvania' },
  { value: 'RI', name: 'Rhode Island' },
  { value: 'SC', name: 'South Carolina' },
  { value: 'SD', name: 'South Dakota' },
  { value: 'TN', name: 'Tennessee' },
  { value: 'TX', name: 'Texas' },
  { value: 'UT', name: 'Utah' },
  { value: 'VA', name: 'Virginia' },
  { value: 'VT', name: 'Vermont' },
  { value: 'WA', name: 'Washington' },
  { value: 'WI', name: 'Wisconsin' },
  { value: 'WV', name: 'West Virginia' },
  { value: 'WY', name: 'Wyoming' },
]

// A-Za-z: allows only English letters
// ': allows apostrophes
// \\-: allows hyphens
// space: allows spaces
export const namePatternMatch = "^[A-Za-z'\\- ]+$"

export const getSinglarOrPluralWord = (amount, text = 'Ticket') => {
  const pluralChars = text === 'Guest Pass' ? 'es' : 's'
  return isPlural(amount) ? `${text}${pluralChars}` : `${text}`
}

export const isPlural = (amount) => {
  return amount === 0 || amount > 1
}

// currency changes to what the host is set to
// rounds decimals to 2
// adds comma separator
export const formatCurrency = (num) => {
  return parseFloat(num ? num : 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** Seat list / tooltips: complimentary offers display as $0.00 */
export const formatOfferListPrice = (amount, offer) => {
  return formatCurrency(offer?.freeOffer ? 0 : amount)
}

const seatIdCount = (ids) => (Array.isArray(ids) ? ids.length : 0)

/** Inventory fill for listing urgency bars (parity with blocktickets-new). */
export const getInventoryFill = (group) => {
  const capacity = seatIdCount(group?.allSeatIds)
  if (capacity <= 0) return null

  const available = group?.GA
    ? Math.max(0, Number(group.availableCount) || 0)
    : seatIdCount(group?.seatIds)

  const sold = Math.max(0, capacity - Math.min(available, capacity))
  const fullPct = Math.min(100, Math.round((sold / capacity) * 100))
  return { available, capacity, fullPct }
}

export const formatNumber = (num) => {
  return parseFloat(num || 0).toLocaleString()
}

export const capitalizeString = (str) => {
  return str
    ?.toLowerCase()
    .split(' ')
    .map(function (word) {
      return word?.charAt(0).toUpperCase() + word?.slice(1)
    })
    .join(' ')
}

// sort tickets numerically
// used to display tickets in my wallet pages
const compareAlphanumeric = (a, b) => {
  // Use localeCompare with numeric option for natural sorting
  // Treat null/undefined as empty strings for comparison
  const valA = String(a ?? '')
  const valB = String(b ?? '')
  return valA.localeCompare(valB, undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

// Include in each ticket a property: eventStart ( event.start ) for grouping early first by event
export const sortTickets = (tickets) => {
  return [...(tickets || [])].sort((a, b) => {
    // Ensure we have valid objects
    if (!a || !b) return 0

    // PRIMARY: Sort by eventStart (earliest events first)
    const aEventStart = a.eventStart || ''
    const bEventStart = b.eventStart || ''
    if (aEventStart && bEventStart) {
      const eventStartCompare = new Date(aEventStart) - new Date(bEventStart)
      if (eventStartCompare !== 0) {
        return eventStartCompare
      }
    }

    // SECONDARY: Group tickets by eventId (keeps same event together)
    const aEventId = a.eventId || ''
    const bEventId = b.eventId || ''
    const eventIdCompare = aEventId.localeCompare(bEventId)
    if (eventIdCompare !== 0) {
      return eventIdCompare
    }

    // WITHIN EACH EVENT GROUP: Sort by seating details

    // 1. General Admission tickets come first within the event
    const aGA = Boolean(a.generalAdmission)
    const bGA = Boolean(b.generalAdmission)

    if (aGA && !bGA) {
      return -1 // a (GA) comes first
    }
    if (!aGA && bGA) {
      return 1 // b (GA) comes first
    }

    // 2. If both are GA, sort by section only
    if (aGA && bGA) {
      return compareAlphanumeric(a.sectionNumber, b.sectionNumber)
    }

    // 3. For assigned seating: section → row → seat
    const sectionCompare = compareAlphanumeric(a.sectionNumber, b.sectionNumber)
    if (sectionCompare !== 0) {
      return sectionCompare
    }

    const rowCompare = compareAlphanumeric(a.rowNumber, b.rowNumber)
    if (rowCompare !== 0) {
      return rowCompare
    }

    const seatCompare = compareAlphanumeric(a.seatNumber, b.seatNumber)
    if (seatCompare !== 0) {
      return seatCompare
    }

    // Final tiebreaker for identical tickets
    const aId = String(a.id || a.checkInCode || '')
    const bId = String(b.id || b.checkInCode || '')
    return aId.localeCompare(bId)
  })
}

// sort events by date
export const sort = (arr, obj) => {
  if (!arr) return
  return arr.sort(function (a, b) {
    // orders, guestlist and events array have different object structures - have extra obj
    if (obj) {
      a = a[obj]?.start
      b = b[obj]?.start
    } else {
      a = a?.start
      b = b?.start
    }
    // Turn your strings into dates, and then subtract them
    // to get a value that is either negative, positive, or zero.
    return new Date(a) - new Date(b)
  })
}

// address - city, state
export const formatShortAddress = (obj) => {
  return `${capitalizeString(
    obj?.address[0]?.city
  )}, ${obj?.address[0]?.state?.toUpperCase()}`
}

// format full address
export const formatAddress = (obj) => {
  return `${obj?.address[0]?.address_1}, ${capitalizeString(
    obj?.address[0]?.city
  )}, ${obj?.address[0]?.state.toUpperCase()}, ${
    obj?.address[0]?.zipcode
  }, ${obj?.address[0]?.country.toUpperCase()}`
}

// whether to show full start date or just start time
export const getStartDateFormatter = (obj) => {
  return !obj?.display_start_time ? 'dateOnly' : ''
}

// format short date for cards and search items with no year
// display only date and start time
export const formatShortDate = (date, formatter) => {
  if (!date) return
  switch (formatter) {
    case 'dateOnly':
      return date?.format('MMM D')

    case 'dateOnlyWithDay':
      return date?.format('ddd, MMM D')

    case 'weekdayOnly':
      return date?.format('ddd')

    case 'dayOnly':
      return date?.format('D')

    case 'monthOnly':
      return date?.format('MMM')

    default:
      return date?.format('MMM D, h:mm A')
  }
}
// format full date
// display full date with start and end time and/or time only
// assumed an event is one day
export const formatDateTime = (date, formatter) => {
  if (!date) return
  switch (formatter) {
    case 'dateOnly':
      return date?.format('ddd, MMM D, YYYY')

    case 'timeOnly':
      return date?.format('h:mm A')

    case 'seoDate':
      return date?.format('MMM DD, YYYY')

    default:
      return date?.format('ddd, MMM D, YYYY h:mm A')
  }
}

// get current time in specific timezone
export const getNowInTimezone = (timezone) => {
  if (!timezone) return moment()
  const tz = SharedDateTime.ensureIanaTimeZone(timezone, 'UTC')
  // Return a moment object representing 'now' in the target timezone
  const now = new Date()
  const zoned = SharedDateTime.toZonedTime(now.toISOString(), tz)
  return moment(zoned)
}

// converts date to timezone using IANA tz (no fixed offsets)
// returns moment object - used with other format dates helper methods
export const getTimezoneDate = (date, timezone) => {
  if (!date || !timezone) return
  const tz = SharedDateTime.ensureIanaTimeZone(timezone, 'UTC')
  try {
    const inputIso =
      typeof date === 'string'
        ? date
        : date instanceof Date
          ? date.toISOString()
          : moment(date).toISOString()
    // If tz resolution failed to something non-IANA and we have a numeric offset, fallback
    if (
      tz === 'UTC' &&
      typeof timezone?.offset === 'number' &&
      !timezone?.abbr
    ) {
      return moment(inputIso).utcOffset(timezone.offset * 60, false)
    }
    const zoned = SharedDateTime.toZonedTime(inputIso, tz)
    return moment(zoned)
  } catch (_e) {
    if (typeof timezone?.offset === 'number') {
      return moment(date).utcOffset(timezone.offset * 60, false)
    }
    return moment(date)
  }
}

export const isEventDateTbd = (event) =>
  Boolean(event?.date_tbd || event?.start_tbd)

export const isEventTimeTbd = (event) =>
  isEventDateTbd(event) || Boolean(event?.time_tbd)

export const isEventStartTbd = isEventDateTbd

export const formatEventSchedule = (event, timezone) => {
  if (isEventDateTbd(event)) return 'Date & Time TBD'
  const zoned = getTimezoneDate(event?.start, timezone)
  if (!zoned?.format) return ''
  const dateLabel = zoned.format('MMM D')
  if (isEventTimeTbd(event)) return `${dateLabel} · Time TBD`
  if (event?.display_start_time === false) return dateLabel
  return `${dateLabel} · ${zoned.format('h:mm A')}`
}

export const formatEventStart = (event, timezone, formatter) => {
  if (isEventDateTbd(event)) return 'Date & Time TBD'
  const zoned = getTimezoneDate(event?.start, timezone)
  if (isEventTimeTbd(event)) {
    if (formatter === 'timeOnly') return 'Time TBD'
    if (formatter === 'dateOnly') return formatDateTime(zoned, formatter)
    return formatEventSchedule(event, timezone)
  }
  return formatDateTime(zoned, formatter)
}

export const formatEventStartShort = (event, timezone, formatter) => {
  if (isEventDateTbd(event)) return 'Date & Time TBD'
  const zoned = getTimezoneDate(event?.start, timezone)
  if (
    isEventTimeTbd(event) &&
    formatter !== 'monthOnly' &&
    formatter !== 'dayOnly' &&
    formatter !== 'weekdayOnly'
  ) {
    const dateLabel = formatShortDate(
      zoned,
      formatter === 'dateOnlyWithDay' ? 'dateOnlyWithDay' : 'dateOnly'
    )
    return dateLabel ? `${dateLabel} · Time TBD` : 'Time TBD'
  }
  return formatShortDate(zoned, formatter)
}

// calls getTimezoneDate to convert date to timezone
// returns string - used for date inputs and to check if two dates are equal
export const getFormattedTimezoneDate = (date, timezone) => {
  if (!date || !timezone) return
  const tz = SharedDateTime.ensureIanaTimeZone(timezone, 'UTC')
  const inputIso =
    typeof date === 'string'
      ? date
      : date instanceof Date
        ? date.toISOString()
        : moment(date).toISOString()
  // Format as "YYYY-MM-DD HH:mm:ss" in the target timezone
  return SharedDateTime.formatInTimeZone(inputIso, tz, 'yyyy-MM-dd HH:mm:ss')
}

// Convert a local date/time in a given timezone to UTC ISO string (Z)
// date can be a Date, moment, or ISO-like string representing the local wall time in `timezone`
export const toUtcIsoFromTimezone = (date, timezone) => {
  if (!date || !timezone) return
  const tz = SharedDateTime.ensureIanaTimeZone(timezone, 'UTC')
  const localIso =
    typeof date === 'string' ? date : moment(date).format('YYYY-MM-DDTHH:mm:ss')
  // Returns an ISO string in UTC (with Z)
  return SharedDateTime.toUtcFromZoned(localIso, tz)
}

export const emailPatternMatch = (val) => {
  // email regex pattern
  const emailPattern = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/g
  return val ? new RegExp(emailPattern).test(val) : true
}

export const getUrl = (link, isGAOnlyEvent) => {
  return isGAOnlyEvent ? link : `${link}/tickets`
}

// Reserved single-segment paths that are not organization landing pages
const RESERVED_ORG_SLUGS = new Set([
  'browse',
  'login',
  'search',
  'checkout',
  'settings',
  'www',
  'menu',
  'group',
  'fundraise',
  'privacy-policy',
  'terms-conditions',
  'purchase-policy',
  'cookies-policy',
  'disclaimer',
  'my-events',
  'my-transfers',
  'my-listings',
  'my-collectables',
  'my-packages',
  'guest-passes',
  'event-details',
  'tickets',
])

export const isOrganizationLandingPage = (url) => {
  const match = url.match(/^\/([^/]+)\/?$/)
  if (!match) return false
  return !RESERVED_ORG_SLUGS.has(match[1].toLowerCase())
}

export const isPackagePurchasePage = (url) => {
  return /\/(?:package|flex-pack)\/[^/]+\/?$/.test(url)
}

// check to see if page should be hide navigation
export const shouldHideNav = (url) => {
  var expression = /(\/e\/|\/tickets|\/t\/|\/checkout|\/menu\/)/g
  var regex = new RegExp(expression)
  return regex.test(url)
}

// hide navigation on all screen sizes (menu + purchase pages + org landing)
export const shouldFullyHideNav = (url) => {
  return (
    /^\/menu\//.test(url) ||
    /\/e\/[^/]+\/[^/]+\/tickets/.test(url) ||
    /^\/e\/[^/]+\/[^/]+\/?$/.test(url) ||
    isOrganizationLandingPage(url) ||
    isPackagePurchasePage(url)
  )
}

export const isFundraisingCampaignPage = (url) => {
  return /^\/fundraise\//.test(url) || /\/fundraisers\//.test(url)
}

// check to see if page should not have container padding on mobile
export const noMobilePadding = (url) => {
  var expression = /(\/tickets|\/t\/|\/checkout|\/menu\/)/g
  var regex = new RegExp(expression)
  return regex.test(url)
}

// check to see if page should be full height
export const checkUrl = (url) => {
  var expression = /(\/e\/|\/venue\/|\/tickets|\/event-details|\/menu\/)/g
  var regex = new RegExp(expression)
  return (
    regex.test(url) ||
    isOrganizationLandingPage(url) ||
    isPackagePurchasePage(url)
  )
}

export const fullHeightContainer = (el) => {
  el.classList.add('container--full-height')

  const root = document.getElementById('root')

  root.classList.add('full-height-flex')
}

export const removeFullHeightContainer = (el) => {
  el.classList.remove('container--full-height')

  const root = document.getElementById('root')

  root.classList.remove('full-height-flex')
}

export const toggleNavContent = (show, ...rest) => {
  rest.forEach((el) => (el.style.visibility = show ? 'visible' : 'hidden'))
}

export const toggleElement = (el, show) => {
  el.style.display = show ? 'block' : 'none'
}

// get accessible label associated with ticket
export const getAccessibleLabel = (type) => {
  if (type === 'DA') return 'Accessible: Open space for wheelchair'
  if (type === 'DB') return 'Accessible: Chair-back seating'
  return 'Accessible seating'
}

export const getSeatAdditionalLabel = ({
  mapSeat,
  ticket,
  ticketGroup,
  seatLabels,
} = {}) => {
  const seatId = ticket?.seatId || ticketGroup?.seatId || mapSeat?.seatId
  return (
    ticket?.additionalLabel ||
    ticketGroup?.additionalLabel ||
    mapSeat?.additionalLabel ||
    (seatId && seatLabels?.[seatId]) ||
    ''
  )
}

export const cartTotal = (cart, processingFee, tax) => {
  let total
  if (cart.listing) {
    let totalTicketPrices = Number(
      parseFloat(
        cart.listing.askingPrice * cart.listing.tickets.length
      ).toFixed(2)
    )
    let fees = Number(
      parseFloat(
        cart?.listing?.tickets[0]?.fee +
          cart?.listing?.tickets[0]?.facilityFee +
          processingFee +
          tax
      ).toFixed(2)
    )
    total = totalTicketPrices + fees
  } else if (cart.ticket) {
    let totalTicketPrices = Number(
      parseFloat(
        cart.ticket.resale
          ? cart.ticket.listingAskingPrice
          : cart.ticket.cost * cart.ticketCount
      ).toFixed(2)
    )
    let fees = Number(
      parseFloat(
        cart.ticket.fee * cart.ticketCount +
          cart.ticket.facilityFee * cart.ticketCount +
          processingFee +
          tax
      ).toFixed(2)
    )
    total = totalTicketPrices + fees
  }

  return total
}

export const getProcessingFeeFromChargeAmount = (chargeAmount) => {
  const amount = Number(chargeAmount || 0)
  if (!Number.isFinite(amount) || amount <= 0) return 0

  const stripeCharge = 0.3
  const stripeServicePercentage = 0.029
  return Number((stripeCharge + amount * stripeServicePercentage).toFixed(2))
}

// used in event details and invoice
// remove duplicate tickets in packages
export const removeDuplicateTickets = (tickets) => {
  const uniqueTickets = new Map()

  tickets.forEach((ticket) => {
    const key = `${ticket.sectionNumber}-${ticket?.rowNumber}-${ticket?.seatNumber}`
    if (!uniqueTickets.has(key)) {
      uniqueTickets.set(key, ticket)
    }
  })

  return Array.from(uniqueTickets.values())
}

export const userDevice = () => {
  let hasTouchScreen = false
  if ('maxTouchPoints' in navigator) {
    hasTouchScreen = navigator.maxTouchPoints > 0
  } else if ('msMaxTouchPoints' in navigator) {
    hasTouchScreen = navigator.msMaxTouchPoints > 0
  } else {
    const mQ = window.matchMedia && matchMedia('(pointer:coarse)')
    if (mQ && mQ.media === '(pointer:coarse)') {
      hasTouchScreen = !!mQ.matches
    } else if ('orientation' in window) {
      hasTouchScreen = true // deprecated, but good fallback
    } else {
      // Only as a last resort, fall back to user agent sniffing
      var UA = navigator.userAgent
      hasTouchScreen =
        /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
        /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA)
    }
  }
  if (hasTouchScreen) {
    return 'Mobile'
  } else {
    return 'Desktop'
  }
}

export const timezones = (abbr) => {
  let zones = [
    {
      value: 'Eastern Daylight Time',
      abbr: 'EDT',
      offset: -4,
      isdst: true,
      text: '(UTC-04:00) Eastern Daylight Time (US & Canada)',
      utc: [
        'America/Detroit',
        'America/Havana',
        'America/Indiana/Petersburg',
        'America/Indiana/Vincennes',
        'America/Indiana/Winamac',
        'America/Iqaluit',
        'America/Kentucky/Monticello',
        'America/Louisville',
        'America/Montreal',
        'America/Nassau',
        'America/New_York',
        'America/Nipigon',
        'America/Pangnirtung',
        'America/Port-au-Prince',
        'America/Thunder_Bay',
        'America/Toronto',
      ],
      stateAbbreviation: [
        'me',
        'ny',
        'pa',
        'mi',
        'in',
        'oh',
        'wv',
        'va',
        'nc',
        'sc',
        'ga',
        'fl',
        'nj',
        'tn',
        'md',
        'ri',
        'ma',
      ],
    },
    {
      value: 'Central Standard Time',
      abbr: 'CDT',
      offset: -5,
      isdst: true,
      text: '(UTC-06:00) Central Time (US & Canada)',
      utc: [
        'America/Chicago',
        'America/Texas',
        'America/Louisiana',
        'America/Indiana/Knox',
        'America/Indiana/Tell_City',
        'America/Matamoros',
        'America/Menominee',
        'America/North_Dakota/Beulah',
        'America/North_Dakota/Center',
        'America/North_Dakota/New_Salem',
        'America/Rainy_River',
        'America/Rankin_Inlet',
        'America/Resolute',
        'America/Winnipeg',
        'CST6CDT',
      ],
      stateAbbreviation: [
        'tx',
        'la',
        'ar',
        'ms',
        'al',
        'ok',
        'ks',
        'mo',
        'il',
        'wi',
        'mn',
        'nd',
        'sd',
        'ne',
        'ia',
        'ky',
      ],
    },
    {
      value: 'Mountain Standard Time',
      abbr: 'MDT',
      offset: -6,
      isdst: true,
      text: '(UTC-07:00) Mountain Time (US & Canada)',
      utc: [
        'America/Boise',
        'America/Cambridge_Bay',
        'America/Denver',
        'America/Edmonton',
        'America/Inuvik',
        'America/Ojinaga',
        'America/Yellowknife',
        'MST7MDT',
      ],
      stateAbbreviation: ['mt', 'id', 'wy', 'ut', 'co', 'az', 'nm'],
    },
    {
      value: 'Pacific Standard Time',
      abbr: 'PST',
      offset: -8,
      isdst: false,
      text: '(UTC-08:00) Pacific Standard Time (US & Canada)',
      utc: [
        'America/Los_Angeles',
        'America/Tijuana',
        'America/Vancouver',
        'PST8PDT',
      ],
      stateAbbreviation: ['wa', 'or', 'ca', 'nv'],
    },
  ]

  let offset = zones.find((zone) => zone.abbr === abbr)

  return offset
}

export const isBlockedEmail = (email) => {
  // these bot emails are flooding system need to supress them
  let badDomains = [
    'protonbox.pro',
    'ultramail.pro',
    'mypost.lol',
    'e-boss.xyz',
    'mailgod.xyz',
    'gopostal.top',
    'e-mail.lol',
    'gogomail.ink',
    'anymail.xyz',
    'blueink.top',
  ]
  let emailDomain = email.split('@')[1]
  let isRussian = emailDomain.split('.')[1].includes('ru')
  let isUkraine = emailDomain.split('.')[1].includes('ua')

  if (isRussian || isUkraine) {
    return true
  } else {
    return badDomains.includes(emailDomain)
  }
}
// OFFER and PACKAGE are both just offer here; since the values I need are interchangeable for this function
export const getValidQuantitiesForTicketGroup = (
  offer,
  availableSeatsLength,
  eventTicketLimit
) => {
  if (!offer) {
    const max = Math.min(
      eventTicketLimit || 100,
      availableSeatsLength || 0,
      100
    )
    const quantities = []
    for (let i = 1; i <= max; i += 1) quantities.push(i)
    return quantities
  }

  const limit = offer.limit
  const minQuantity = offer.minQuantity || 0
  const maxQuantity = Math.min(offer.maxQuantity || 100, 100)
  const multipleOf = offer.multipleOf || 1

  // If limit is set (and not 0), return just that value
  if (limit > 0) {
    return limit <= availableSeatsLength ? [limit] : []
  }

  // Calculate the effective maximum (considering eventTicketLimit and availableSeats)
  const effectiveMax = Math.min(
    maxQuantity,
    eventTicketLimit || 100,
    availableSeatsLength
  )

  // Generate array of valid quantities
  const quantities = []
  for (let i = minQuantity; i <= effectiveMax; i++) {
    if (i % multipleOf === 0) {
      quantities.push(i)
    }
  }

  return quantities
}

// get ticket object
// used in detail modal and invoice
export const getTicket = (object, field) => {
  let obj

  // guest pass
  if (object?.guest_passes) {
    obj = object?.guest_passes[0]
  }
  // transfer and exchange tickets
  else if (
    object?.status === 'completeFromTransfer' ||
    (object?.status === 'complete' &&
      (object?.source === 'transfer' ||
        object?.source === 'ticket_exchange')) ||
    object?.status === 'completeFromExchange'
  )
    obj = object?.tickets ? object.tickets[0] : object?.details?.details?.ticket
  // resale tickets - sold listing in my listings and invoice listing are different objects
  else if (object?.type === 'resale' || object?.askingPrice)
    obj = object?.tickets[0]
  // primary tickets
  else obj = object?.details?.ticket

  if (field) {
    return obj[field]
  } else {
    return obj
  }
}

export const calculateFees = (ticket, feeStructure, taxRates, event) => {
  // Delegate to shared-utils centralized calculator to keep logic consistent across projects
  try {
    return SharedFees.calculateItemFees(ticket, feeStructure, taxRates, event)
  } catch (e) {
    // Fallback: simple zeroed breakdown if shared-utils is not available at runtime
    const toNum = (v) => {
      const n = typeof v === 'string' ? parseFloat(v) : Number(v)
      return Number.isFinite(n) ? n : 0
    }
    const ticketCost = toNum(ticket?.offerPrice)
    return {
      ticketCost,
      serviceFees: 0,
      serviceFee: 0,
      ticketFacilityFee: 0,
      taxPerTicket: 0,
      salesTax: 0,
      paymentProcessingFee: 0,
      chargeBackProtection: 0,
      ticketCostWithFees: ticketCost,
      ticketCostWithFeesAndTax: ticketCost,
      totalFees: 0,
      totalDue: ticketCost,
      combinedTaxRate: 0,
      payout: ticketCost,
    }
  }
}
